import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {
  StyledExpenses,
  StyledExpensesMoreButton,
  StyledExpensesContainer,
  StyledExpensesEmpty
} from '../Expenses/StyledExpenses';
import Spin from '../../../Spin';
import Table from '../../../Table';
import {ExpensesIcon} from '../../../../icons';
import { budgetStatusConstants } from '../../../../constants';
import {useIsCompanyChanged} from '../../../../hooks';
import {ReactComponent as EmptySvgImage} from '../../../../static/images/pages/overview/empty-expenses.svg';
import {budgetActions} from '../../../../state/actions/budget.actions';
import {amountHelpers, budgetHelpers, dateHelpers} from '../../../../utils/helpers';
import routes from '../../../../routes/routes.json';
import overviewShared from '../OverviewShared/overviewShared';


const tableKeys = {
  NEED_YOUR_APPROVAL: 'need-your-approval',
}

const { NEED_YOUR_APPROVAL } = tableKeys;

const columnProps = { width: 'calc(100% / 5)' };

const Budgets = ({ ...rest }) => {
  const [t] = useTranslation(['main', 'subscriptions']);
  const [approvalTableProps, setApprovalTableProps] = useState(overviewShared.defaultTableProps);

  const navigate = useNavigate();

  const companyId = useIsCompanyChanged();

  const dispatch = useDispatch();

  const {employeeEmail} = useSelector(({ user: { employee }}) => ({ employeeEmail: employee?.email }));

  const isEmpty = useMemo(() => !approvalTableProps.data.length && !approvalTableProps.loading, [approvalTableProps]); // eslint-disable-line react-hooks/exhaustive-deps

  const isEnableLoading = useMemo(() => !approvalTableProps.data.length && approvalTableProps.loading, [approvalTableProps]); // eslint-disable-line react-hooks/exhaustive-deps

  const getQueryParams = (key) => ({
    [NEED_YOUR_APPROVAL]: {
      active_approver: employeeEmail,
      per_page: 3,
      status: budgetStatusConstants.IN_REVIEW
    },
  }[key] || {});

  const loadTableData = overviewShared.createLoadTableData(({ query, successCallback, errorCallback }) => {
    dispatch(budgetActions.getBudgetRequests({ query: { ...query }, successCallback, errorCallback }));
  }, getQueryParams, (data) => data?.results || []);

  useEffect(() => {
    if (!companyId) return;

    loadTableData({key: NEED_YOUR_APPROVAL, setter: setApprovalTableProps, initial: true});
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = (key, cursor) => {
    const setter = {
      [NEED_YOUR_APPROVAL]: setApprovalTableProps,
    }[key] || undefined
    if (!setter) return;

    loadTableData({key, setter, query: { cursor }})
  }

  const getLabel = overviewShared.getLabelFactory(t, ExpensesIcon);

  const handleRowClick = (record) => navigate(`${routes.budgetsRequests}/${record.id}`);

  const columns = [
    {
      ...columnProps,
      className: 'no-wrap',
      dataIndex: 'name',
      render: (_, budget) => budgetHelpers.getBudgetName(budget),
      title: t('name'),
    },
    {
      ...columnProps,
      dataIndex: 'date',
      width: '150px',
      render: (_, record) => dateHelpers.getDateFromISO(record?.created_at, 'DD MMM YYYY'),
      title: t('date'),
    },
    {
      ...columnProps,
      dataIndex: 'reported_by',
      width: '80px',
      render: budgetHelpers.getOwner,
      title: t('Owner'),
    },
    {
      ...columnProps,
      align: 'right',
      dataIndex: 'limit_amount',
      render: (value) => <div className='amount'>{amountHelpers.getAmountWithCurrencyCode(value)}</div>,
      title: t('amount'),
    },
  ];

  const getTable = ({key, tableProps, showSeeMoreButton = true} = {}) => overviewShared.getTableJSX({
    TableComponent: Table,
    columns,
    tableProps,
    onRowClick: handleRowClick,
    SeeMoreButton: StyledExpensesMoreButton,
    handleLoadMore,
    keyName: key,
    t,
    showSeeMore: showSeeMoreButton
  });

  const items = [
    approvalTableProps.data.length > 0 && {
      children: getTable({key: NEED_YOUR_APPROVAL, tableProps: approvalTableProps}),
      key: NEED_YOUR_APPROVAL,
      label: getLabel('needYourApproval'),
    },
  ].filter(Boolean);

  return (
    <Spin size='small' spinning={isEnableLoading}>
      <StyledExpensesContainer>

        <StyledExpenses ghost items={items} defaultActiveKey={Object.values(tableKeys)} {...rest} />

        {isEmpty && (
          <StyledExpensesEmpty
            image={<EmptySvgImage />}
            description={t('overviewPage.budgets.empty.description')}
            title={t('overviewPage.budgets.empty.title')}
          />
        )}

      </StyledExpensesContainer>
    </Spin>
  );
}

export default Budgets;
